import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, Play, Pause, SkipForward, Sparkles, ArrowRight } from "lucide-react";
import { Immersive, immersiveSession } from "../lib/immersive";

/**
 * SHOW ME THE FUTURE
 * A 90-120 second guided auto-play tour across the 5 experiences.
 * Each experience contributes one 20s "pulse" segment with a cinematic shot and copy.
 */

const PULSE_MS = 4800; // 4.8s per scene; 5 experiences × ~4 scenes = ~96s total

const SEGMENTS = [
  {
    slug: "data-packet",
    accent: "#7CE4FF",
    accentDark: "#0C1E2A",
    scenes: [
      { title: "The internet is a flood of tiny envelopes.", sub: "This is one of them.", visual: "packet" },
      { title: "Routers. Backbones. CDNs.", sub: "The real shape of the internet.", visual: "network" },
      { title: "You pick the path.", sub: "Latency. Loss. TTL. Live.", visual: "route" },
      { title: "A link breaks. BGP reroutes in seconds.", sub: "The internet repairs itself.", visual: "heal" },
    ],
  },
  {
    slug: "human-cell",
    accent: "#C9F26B",
    accentDark: "#1E2A14",
    scenes: [
      { title: "You shrink to 2 microns.", sub: "The cell membrane looms.", visual: "cell" },
      { title: "You dock at a mitochondrion.", sub: "The Krebs cycle unfolds in your hand.", visual: "atp" },
      { title: "DNA → RNA. In real time.", sub: "You do the transcription yourself.", visual: "dna" },
    ],
  },
  {
    slug: "aircraft",
    accent: "#FFC66B",
    accentDark: "#2C1E0E",
    scenes: [
      { title: "Lift. Drag. Thrust. Weight.", sub: "All four forces, visible as vectors.", visual: "plane" },
      { title: "You take off.", sub: "You climb. You stall. You recover.", visual: "runway" },
      { title: "By minute 8 — you understand WHY planes fly.", sub: "Not just that they do.", visual: "sky" },
    ],
  },
  {
    slug: "power-plant",
    accent: "#FF9A6B",
    accentDark: "#2E1710",
    scenes: [
      { title: "480 instruments. One core.", sub: "A city of 2 million wants its lights on.", visual: "reactor" },
      { title: "A coolant valve sticks.", sub: "90 seconds. SCRAM or stabilise?", visual: "alert" },
      { title: "You feel the weight of the lights staying on.", sub: "This is operator mentality.", visual: "grid" },
    ],
  },
  {
    slug: "break-ai",
    accent: "#E07AFF",
    accentDark: "#24132E",
    scenes: [
      { title: "Make the AI break its own rules.", sub: "Prompt injection. Jailbreak. Adversarial.", visual: "neural" },
      { title: "Each attack unlocks the guardrail.", sub: "You leave building safer AI.", visual: "shield" },
    ],
  },
];

const ALL_SCENES = SEGMENTS.flatMap((seg) => seg.scenes.map((s) => ({ ...s, slug: seg.slug, accent: seg.accent, accentDark: seg.accentDark })));

export default function ImmersiveFuture() {
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [done, setDone] = useState(false);
  const session = immersiveSession();

  useEffect(() => {
    Immersive.event("future_tour_started", { session_id: session });
  }, [session]);

  useEffect(() => {
    if (!playing || done) return undefined;
    const t = setTimeout(() => {
      if (idx < ALL_SCENES.length - 1) setIdx(idx + 1);
      else { setDone(true); Immersive.event("future_tour_completed", { session_id: session }); }
    }, PULSE_MS);
    return () => clearTimeout(t);
  }, [idx, playing, done, session]);

  const scene = ALL_SCENES[idx];
  const totalSec = Math.round((ALL_SCENES.length * PULSE_MS) / 1000);
  const nowSec = Math.round(((idx + 1) * PULSE_MS) / 1000);

  if (done) return <TourFinale />;

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ background: `radial-gradient(circle at 30% 30%, ${scene.accentDark} 0%, #040609 70%)`, transition: "background 1.5s" }} data-testid="immersive-future-page">
      {/* backdrop grid */}
      <svg className="absolute inset-0 w-full h-full opacity-30 pointer-events-none" preserveAspectRatio="none">
        <defs>
          <pattern id="fg" width="60" height="60" patternUnits="userSpaceOnUse">
            <path d="M 60 0 L 0 0 0 60" fill="none" stroke={`${scene.accent}20`} strokeWidth="0.4" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#fg)" />
      </svg>

      {/* glow */}
      <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full blur-3xl pointer-events-none" style={{ background: `radial-gradient(circle, ${scene.accent}22 0%, transparent 70%)`, transition: "background 1.5s" }} />

      <div className="relative max-w-6xl mx-auto px-5 md:px-10 min-h-screen flex flex-col justify-center">
        {/* top rail */}
        <div className="flex items-center gap-3 mb-10">
          <Link to="/immersive" className="text-[11px] tracking-[0.22em] uppercase font-bold" style={{ color: scene.accent }} data-testid="future-back">
            ← Back to lab
          </Link>
          <div className="h-5 w-px bg-[#2A3349]" />
          <div className="text-[10px] tracking-[0.28em] uppercase font-bold" style={{ color: scene.accent }}>
            SHOW ME THE FUTURE · {nowSec}s / {totalSec}s
          </div>
        </div>

        {/* main scene */}
        <AnimatePresence mode="wait">
          <motion.div
            key={idx}
            initial={{ opacity: 0, scale: 0.96, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 1.02, y: -10 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="text-center"
          >
            <SceneVisual visual={scene.visual} accent={scene.accent} />
            <div className="mt-10 text-[11px] tracking-[0.3em] uppercase font-bold" style={{ color: scene.accent }}>
              EXPERIENCE · {scene.slug.replace("-", " ").toUpperCase()}
            </div>
            <h1 className="font-mythos text-4xl md:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[1.0] text-[#F4EFE6] mt-5 max-w-4xl mx-auto">
              {scene.title}
            </h1>
            <p className="font-mythos text-lg md:text-2xl text-[#F4EFE6]/85 mt-5 max-w-2xl mx-auto">{scene.sub}</p>
          </motion.div>
        </AnimatePresence>

        {/* segment chapters */}
        <div className="mt-16 flex flex-wrap items-center gap-2 justify-center">
          {ALL_SCENES.map((s, i) => (
            <div key={i} className="h-1 rounded-full transition-all" style={{
              width: i === idx ? 40 : 14,
              background: i <= idx ? s.accent : "#2A3349",
            }} />
          ))}
        </div>

        {/* controls */}
        <div className="mt-10 flex items-center justify-center gap-4">
          <button
            onClick={() => setPlaying(!playing)}
            className="rounded-full w-12 h-12 grid place-items-center"
            style={{ background: scene.accent, color: "#0E0F12" }}
            data-testid="future-play-pause"
          >
            {playing ? <Pause size={16} /> : <Play size={16} />}
          </button>
          <button
            onClick={() => { if (idx < ALL_SCENES.length - 1) setIdx(idx + 1); else setDone(true); }}
            className="rounded-full w-12 h-12 grid place-items-center border transition"
            style={{ borderColor: scene.accent, color: scene.accent }}
            data-testid="future-skip"
          >
            <SkipForward size={16} />
          </button>
          <button onClick={() => setDone(true)} className="text-[11px] tracking-[0.22em] uppercase font-bold" style={{ color: scene.accent }} data-testid="future-jump-end">
            Jump to end →
          </button>
        </div>
      </div>
    </div>
  );
}

// ------------------- scene visuals -------------------
function SceneVisual({ visual, accent }) {
  const common = { style: { color: accent } };
  return (
    <div className="w-28 h-28 md:w-36 md:h-36 mx-auto rounded-full grid place-items-center" style={{ background: `${accent}11`, border: `1.5px solid ${accent}66`, boxShadow: `0 0 60px ${accent}33` }}>
      <svg width="80" height="80" viewBox="0 0 100 100" {...common}>
        {visual === "packet" && (
          <>
            <rect x="30" y="35" width="40" height="30" rx="4" fill="none" stroke={accent} strokeWidth="2" />
            <line x1="30" y1="47" x2="70" y2="47" stroke={accent} strokeWidth="1.2" />
            <circle cx="18" cy="50" r="2" fill={accent}><animate attributeName="cx" values="18;82;18" dur="2s" repeatCount="indefinite" /></circle>
          </>
        )}
        {visual === "network" && (
          <>
            <circle cx="20" cy="30" r="4" fill={accent} />
            <circle cx="50" cy="60" r="5" fill={accent} />
            <circle cx="80" cy="30" r="4" fill={accent} />
            <circle cx="80" cy="80" r="4" fill={accent} />
            <line x1="20" y1="30" x2="50" y2="60" stroke={accent} strokeWidth="1.2" />
            <line x1="50" y1="60" x2="80" y2="30" stroke={accent} strokeWidth="1.2" />
            <line x1="50" y1="60" x2="80" y2="80" stroke={accent} strokeWidth="1.2" />
          </>
        )}
        {visual === "route" && (
          <>
            <path d="M 15 50 Q 40 20 60 50 T 85 50" stroke={accent} strokeWidth="2" fill="none" />
            <circle r="3" fill={accent}>
              <animateMotion dur="2s" repeatCount="indefinite" path="M 15 50 Q 40 20 60 50 T 85 50" />
            </circle>
          </>
        )}
        {visual === "heal" && (
          <>
            <line x1="20" y1="50" x2="80" y2="50" stroke={accent} strokeWidth="2" strokeDasharray="4 3" />
            <circle cx="50" cy="50" r="8" fill="none" stroke={accent} strokeWidth="1.5"><animate attributeName="r" values="8;20;8" dur="1.8s" repeatCount="indefinite" /><animate attributeName="opacity" values="1;0;1" dur="1.8s" repeatCount="indefinite" /></circle>
          </>
        )}
        {visual === "cell" && (
          <>
            <circle cx="50" cy="50" r="32" fill="none" stroke={accent} strokeWidth="2" strokeDasharray="2 2" />
            <circle cx="50" cy="50" r="12" fill={accent} opacity="0.4" />
            <circle cx="30" cy="40" r="4" fill={accent} opacity="0.7" />
            <circle cx="70" cy="55" r="5" fill={accent} opacity="0.7" />
          </>
        )}
        {visual === "atp" && (
          <>
            <polygon points="50,20 70,40 70,60 50,80 30,60 30,40" fill="none" stroke={accent} strokeWidth="2" />
            <text x="50" y="56" fontSize="18" fill={accent} textAnchor="middle" fontWeight="bold">ATP</text>
          </>
        )}
        {visual === "dna" && (
          <>
            <path d="M 30 20 Q 50 40 30 60 Q 50 80 30 100" stroke={accent} strokeWidth="2" fill="none" />
            <path d="M 70 20 Q 50 40 70 60 Q 50 80 70 100" stroke={accent} strokeWidth="2" fill="none" />
            {[30, 50, 70].map((y) => <line key={y} x1="30" y1={y} x2="70" y2={y} stroke={accent} strokeWidth="1" />)}
          </>
        )}
        {visual === "plane" && (
          <>
            <polygon points="20,50 70,45 85,50 70,55" fill={accent} />
            <polygon points="40,45 55,30 55,45" fill={accent} opacity="0.6" />
            <polygon points="40,55 55,70 55,55" fill={accent} opacity="0.6" />
          </>
        )}
        {visual === "runway" && (
          <>
            <line x1="20" y1="80" x2="80" y2="80" stroke={accent} strokeWidth="2" />
            <polygon points="40,78 60,70 70,78" fill={accent}><animateTransform attributeName="transform" type="translate" from="-20 0" to="40 -40" dur="2s" repeatCount="indefinite" /></polygon>
          </>
        )}
        {visual === "sky" && (
          <>
            <circle cx="75" cy="30" r="10" fill={accent} opacity="0.3" />
            <polygon points="25,55 55,52 65,55 55,58" fill={accent} />
          </>
        )}
        {visual === "reactor" && (
          <>
            <circle cx="50" cy="50" r="28" fill="none" stroke={accent} strokeWidth="2" />
            <circle cx="50" cy="50" r="10" fill={accent} opacity="0.6"><animate attributeName="opacity" values="0.3;0.9;0.3" dur="1s" repeatCount="indefinite" /></circle>
            {[0, 1, 2, 3].map((i) => {
              const a = (i / 4) * Math.PI * 2;
              return <circle key={i} cx={50 + Math.cos(a) * 28} cy={50 + Math.sin(a) * 28} r="2.5" fill={accent} />;
            })}
          </>
        )}
        {visual === "alert" && (
          <>
            <polygon points="50,20 80,70 20,70" fill="none" stroke={accent} strokeWidth="2.5"><animate attributeName="stroke" values={`${accent};#FF4444;${accent}`} dur="0.8s" repeatCount="indefinite" /></polygon>
            <line x1="50" y1="38" x2="50" y2="55" stroke={accent} strokeWidth="2.5" />
            <circle cx="50" cy="63" r="2" fill={accent} />
          </>
        )}
        {visual === "grid" && (
          <>
            {[0, 1, 2, 3].map((i) => <line key={`h-${i}`} x1="15" y1={25 + i * 15} x2="85" y2={25 + i * 15} stroke={accent} strokeWidth="1" opacity="0.5" />)}
            {[0, 1, 2, 3].map((i) => <line key={`v-${i}`} x1={25 + i * 15} y1="20" x2={25 + i * 15} y2="80" stroke={accent} strokeWidth="1" opacity="0.5" />)}
            <circle cx="50" cy="50" r="4" fill={accent}><animate attributeName="r" values="3;7;3" dur="1.2s" repeatCount="indefinite" /></circle>
          </>
        )}
        {visual === "neural" && (
          <>
            {[20, 50, 80].map((y1) => [50, 80].map((x2) => <line key={`${y1}-${x2}`} x1="20" y1={y1} x2={x2} y2={50} stroke={accent} strokeWidth="0.8" opacity="0.4" />))}
            {[20, 50, 80].map((y) => <circle key={y} cx="20" cy={y} r="3" fill={accent} />)}
            <circle cx="50" cy="50" r="4" fill={accent} />
            <circle cx="80" cy="50" r="4" fill={accent} />
          </>
        )}
        {visual === "shield" && (
          <>
            <path d="M 50 20 L 75 32 V 55 Q 75 72 50 82 Q 25 72 25 55 V 32 Z" fill="none" stroke={accent} strokeWidth="2.5" />
            <path d="M 40 52 L 48 60 L 62 44" stroke={accent} strokeWidth="2.5" fill="none" />
          </>
        )}
      </svg>
    </div>
  );
}

function TourFinale() {
  return (
    <div className="min-h-screen grid place-items-center" style={{ background: "linear-gradient(180deg,#07090D 0%, #0E0F12 100%)" }} data-testid="future-finale">
      <div className="max-w-3xl mx-auto px-5 md:px-10 py-16 text-center">
        <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase mythos-gold font-bold mb-6">
          <Sparkles size={14} /> This is the demo you just watched
        </div>
        <h1 className="font-mythos text-5xl md:text-7xl font-extrabold tracking-tight leading-[1.0] text-[#F4EFE6]">
          Five experiences.<br/><span className="mythos-gold">One engine.</span>
        </h1>
        <p className="font-mythos text-lg md:text-xl text-[#F4EFE6]/80 mt-6 max-w-2xl mx-auto">
          The ExperienceEngine is the real IP. Every new subject is a stages[] file — not a new codebase.
        </p>
        <div className="mt-10 flex flex-wrap justify-center gap-3">
          <Link to="/immersive/data-packet" className="mythos-gold-bg text-[#0E0F12] rounded-full px-7 py-3.5 font-bold inline-flex items-center gap-2 hover:opacity-90 transition" data-testid="finale-try-flagship">
            TRY THE LIVE FLAGSHIP <ArrowRight size={14} />
          </Link>
          <Link to="/xr/partners" className="border border-[#C9A66B] text-[#C9A66B] rounded-full px-7 py-3.5 font-bold inline-flex items-center gap-2 hover:bg-[#C9A66B] hover:text-[#0E0F12] transition" data-testid="finale-partners">
            Partner with us <ArrowRight size={14} />
          </Link>
          <Link to="/immersive" className="rounded-full px-7 py-3.5 font-bold inline-flex items-center gap-2 text-[#F4EFE6]/70 hover:text-[#F4EFE6]" data-testid="finale-back-lab">
            See all five experiences
          </Link>
        </div>
      </div>
    </div>
  );
}
